/**
 * Session summary display rules.
 *
 * A session carries two title-like strings. `session_name` is written once
 * when the note is generated and never changes. `session_gist` is the same
 * text at generation time, but it is the field the session page lets a
 * clinician edit. Every surface that shows "what is this session called"
 * must therefore prefer `session_gist`.
 *
 * Both fields can also carry bookkeeping placeholders rather than a title,
 * and those must fall through to the untitled label instead of being shown
 * as the session's name. Moved here from `src/constants/session_summary`
 * so the session package's list cells and the app share one rule.
 */

/**
 * Stored by the session page when a clinician clears their summary. A summary
 * cannot be deleted, so an emptied one is saved as this instead.
 */
export const SESSION_SUMMARY_FALL_BACK = 'No summary';

/**
 * Placeholder the backend seeds `session_gist` with before a note exists. The
 * real AI summary only lands once the session has been transcribed, so summary
 * surfaces gate their visibility on the gist not being this value.
 */
export const UNKNOWN_SESSION_GIST = 'Unknown Session';

/**
 * The gist a session display-name fallback may show, or `undefined` when the
 * gist is blank or one of the backend's placeholders.
 *
 * Trimmed before it is judged, and the trimmed value is what's returned. A gist
 * of only whitespace is truthy, so an untrimmed check would hand a name chain a
 * blank string it renders as the session's title.
 */
export const getDisplayableSessionGist = (
  gist: string | null | undefined
): string | undefined => {
  const trimmed = gist?.trim();
  return trimmed &&
    trimmed !== SESSION_SUMMARY_FALL_BACK &&
    trimmed !== UNKNOWN_SESSION_GIST
    ? trimmed
    : undefined;
};

/** The two title-like fields a session list row carries. */
export interface SessionSummaryFields {
  session_gist?: string | null;
  session_name?: string | null;
}

/**
 * The summary text a session list row should display, or `undefined` when the
 * caller should show its untitled label.
 *
 * Prefers the editable `session_gist`. Falls back to `session_name` only when
 * the gist is absent, blank, or still the ungenerated `Unknown Session`
 * placeholder: that keeps the list readable while the backend list endpoint is
 * rolling out `session_gist`, and for older rows that never got a gist.
 *
 * A cleared summary is different. The session page stores `No summary` when a
 * clinician deletes the text, and shows it as such. Falling back to
 * `session_name` there would resurrect the AI title the clinician removed, so
 * a cleared gist resolves to the untitled label instead.
 *
 * `session_name` goes through `getDisplayableSessionGist` too, because it can
 * carry the same placeholders.
 */
export const getSessionListSummary = (
  session: SessionSummaryFields
): string | undefined => {
  if (session.session_gist?.trim() === SESSION_SUMMARY_FALL_BACK) return undefined;
  return (
    getDisplayableSessionGist(session.session_gist) ??
    getDisplayableSessionGist(session.session_name)
  );
};
